import { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { CartProvider } from "./lib/cart";
import { SettingsProvider } from "./lib/settings";
import { Toaster } from "sonner";
import PromoStrip from "./components/PromoStrip";
import AppGate from "./components/AppGate";

import Home from "./pages/Home";
import Category from "./pages/Category";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import { Login, Register } from "./pages/Auth";
import Account from "./pages/Account";

// Admin lazy-loaded (separate bundle)
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminAgencies = lazy(() => import("./pages/admin/AdminAgencies"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminSupplements = lazy(() => import("./pages/admin/AdminSupplements"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminGovernorates = lazy(() => import("./pages/admin/AdminGovernorates"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminCampaigns = lazy(() => import("./pages/admin/AdminCampaigns"));
const AdminSales = lazy(() => import("./pages/admin/AdminSales"));
const AdminForms = lazy(() => import("./pages/admin/AdminForms"));
const AdminFormEditor = lazy(() => import("./pages/admin/AdminFormEditor"));
const AdminFormSubmissions = lazy(() => import("./pages/admin/AdminFormSubmissions"));
const AdminNotices = lazy(() => import("./pages/admin/AdminNotices"));
const PublicForm = lazy(() => import("./pages/PublicForm"));
const NoticesOverlay = lazy(() => import("./components/NoticesOverlay"));

const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#191A1D] text-gray-400">
    <div className="w-8 h-8 border-2 border-[#EF1B17] border-t-transparent rounded-full animate-spin" />
  </div>
);

/**
 * Storefront + Admin routes — wrapped in SettingsProvider (which pulls site-settings + notices)
 * and AppGate (which blocks storefront under maintenance).
 */
function StorefrontShell() {
  return (
    <CartProvider>
      <SettingsProvider>
        <AppGate>
          <PromoStrip />
          <Suspense fallback={null}><NoticesOverlay /></Suspense>
          <Toaster position="top-center" richColors closeButton theme="dark" toastOptions={{ style: { fontFamily: "Alexandria" } }} />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/supplements" element={<Category type="supplement" />} />
            <Route path="/sportswear" element={<Category type="clothing" />} />
            <Route path="/accessories" element={<Category type="accessory" />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/account" element={<Account />} />

            <Route path="/admin" element={<Suspense fallback={<AdminFallback />}><AdminLayout /></Suspense>}>
              <Route index element={<Suspense fallback={<AdminFallback />}><AdminDashboard /></Suspense>} />
              <Route path="agencies" element={<Suspense fallback={<AdminFallback />}><AdminAgencies /></Suspense>} />
              <Route path="products/supplement" element={<Suspense fallback={<AdminFallback />}><AdminSupplements /></Suspense>} />
              <Route path="products/:type" element={<Suspense fallback={<AdminFallback />}><AdminProducts /></Suspense>} />
              <Route path="orders" element={<Suspense fallback={<AdminFallback />}><AdminOrders /></Suspense>} />
              <Route path="users" element={<Suspense fallback={<AdminFallback />}><AdminUsers /></Suspense>} />
              <Route path="settings" element={<Suspense fallback={<AdminFallback />}><AdminSettings /></Suspense>} />
              <Route path="governorates" element={<Suspense fallback={<AdminFallback />}><AdminGovernorates /></Suspense>} />
              <Route path="coupons" element={<Suspense fallback={<AdminFallback />}><AdminCoupons /></Suspense>} />
              <Route path="campaigns" element={<Suspense fallback={<AdminFallback />}><AdminCampaigns /></Suspense>} />
              <Route path="sales" element={<Suspense fallback={<AdminFallback />}><AdminSales /></Suspense>} />
              <Route path="forms" element={<Suspense fallback={<AdminFallback />}><AdminForms /></Suspense>} />
              <Route path="forms/new" element={<Suspense fallback={<AdminFallback />}><AdminFormEditor /></Suspense>} />
              <Route path="forms/:id/edit" element={<Suspense fallback={<AdminFallback />}><AdminFormEditor /></Suspense>} />
              <Route path="forms/:id/submissions" element={<Suspense fallback={<AdminFallback />}><AdminFormSubmissions /></Suspense>} />
              <Route path="notices" element={<Suspense fallback={<AdminFallback />}><AdminNotices /></Suspense>} />
            </Route>
          </Routes>
        </AppGate>
      </SettingsProvider>
    </CartProvider>
  );
}

/**
 * Public form shell — completely isolated from SettingsProvider, notices/maintenance state,
 * PromoStrip, storefront layout, cart. Only mounts the form route.
 */
function PublicFormShell() {
  return (
    <>
      <Toaster position="top-center" richColors closeButton theme="dark" toastOptions={{ style: { fontFamily: "Alexandria" } }} />
      <Routes>
        <Route path="/f/:slug" element={<Suspense fallback={<AdminFallback />}><PublicForm /></Suspense>} />
      </Routes>
    </>
  );
}

/**
 * Router-level dispatcher: /f/* → isolated shell; everything else → storefront shell.
 * The path is detected BEFORE SettingsProvider or AppGate mount so no notice/settings
 * fetch is triggered for public form visits.
 */
function AppShell() {
  const location = useLocation();
  const isPublicForm = location.pathname.startsWith("/f/");
  return isPublicForm ? <PublicFormShell /> : <StorefrontShell />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
